import chatDispatcher from 'common/components/chat/data/dispatcher'
import assign from 'object-assign'
import { EventEmitter } from 'events'
import coreUtils from 'common/src/utils/coreUtils'
import { buildPrivateNoteResource, hasPrivateNoteContent } from 'jobs/common/utils/privateNoteUtils'

let conversations = []
let links = null
let openConversationID = null
let pageContextType = null
let jobId = null
let query = null
const CHANGE_EVENT = 'change'
let loading = true
let allConversationsLoaded = false
let sortKey = ''
let conversationType = 'all'

const API = {
  get: (url, data, successDispatch, failureDispatch) => {
    $.ajax({
      url,
      data,
      method: 'GET',
      headers: { 'Accept': 'application/vnd.api+json; version=3' },
    }).done((response) => {
      chatDispatcher.dispatch({ type: successDispatch, data: response })
    }).fail((jqXHR, textStatus, errorThrown) => {
      console.error('AJAX GET failed:', {
        url, data, status: textStatus, error: errorThrown,
        response: jqXHR.responseText
      })
      if (failureDispatch) {
        chatDispatcher.dispatch({ type: failureDispatch, data: jqXHR })
      }
    })
  }
}

function addIncludedDataToConversations(included, newConversations) {
  if (!included || !included.length) {
    return newConversations
  }

  const eois = included.filter((item) => item.type === 'eois')
  const jobs = included.filter((item) => item.type === 'jobs')
  const privateNotes = included.filter((item) => item.type === 'private-notes')

  newConversations.forEach((conversation) => {
    conversation.included = {}

    const eoiId = conversation.relationships.eoi && conversation.relationships.eoi.data && conversation.relationships.eoi.data.id
    const jobId = conversation.relationships.job && conversation.relationships.job.data && conversation.relationships.job.data.id

    conversation.included.eoi = eois.find((eoi) => eoi.id === eoiId) || null
    conversation.included.job = jobs.find((job) => job.id === jobId) || null

    const eoi = conversation.included.eoi
    const noteRelationship = eoi && eoi.relationships && eoi.relationships.privateNote
    const noteRef = noteRelationship && noteRelationship.data

    if (noteRef) {
      conversation.included.privateNote = privateNotes.find(
        (note) => note.id === noteRef.id && note.type === noteRef.type
      ) || null
    }
  })

  return newConversations
}

function conversationExists(id) {
  return conversations.some(c => c.id === id)
  return conversations.indexOf(conversationStore.get(id)) !== -1
}

function updateConversations(payload) {
  const conversationID = payload.data.data.id
  const conversation = payload.data.data
  const included = payload.data.included

  addIncludedDataToConversations(included, [conversation])

  function replaceConversation() {
    let index = conversations.indexOf(conversationStore.get(conversationID))
    conversations[index] = conversation
  }

  function addToBeginningOfConversations() {
    conversations = [...[conversation], ...conversations]

    if (sortKey) {
      conversations = sortConversations(conversations, sortKey)
    }
  }

  if(conversationExists(conversationID)) {
    replaceConversation()
  } else {
    addToBeginningOfConversations()
  }
}

function sortConversations(data, key) {
  const sorted = [...data]

  if (key === 'most-recently') {
    return sorted.sort((a, b) => {
      return b.relationships.latestMessage.data.id - a.relationships.latestMessage.data.id
    })
  }

  if (key === 'least-recently') {
    return sorted.sort((a, b) => {
      return a.relationships.latestMessage.data.id - b.relationships.latestMessage.data.id
    })
  }

  if (key === 'job-category') {
    return sorted.sort((a, b) => getCategoryName(a).localeCompare(getCategoryName(b)))
  }

  return data
}

function setEoiPrivateNoteRelationship(eoi, noteResource) {
  if (!eoi || !eoi.relationships) {
    return
  }

  if (noteResource) {
    eoi.relationships.privateNote = {
      data: { type: 'private-notes', id: noteResource.id }
    }
    return
  }

  eoi.relationships.privateNote = { data: null }
}

function updatePrivateNoteForEoi(eoiId, noteAttributes, noteId) {
  if (eoiId == null) {
    return
  }

  const normalizedEoiId = String(eoiId)
  let changed = false

  conversations.forEach((conversation) => {
    const conversationEoiId = conversation.relationships &&
      conversation.relationships.eoi &&
      conversation.relationships.eoi.data &&
      conversation.relationships.eoi.data.id

    if (String(conversationEoiId) !== normalizedEoiId || !conversation.included) {
      return
    }

    changed = true
    const eoi = conversation.included.eoi

    if (hasPrivateNoteContent(noteAttributes)) {
      const existingNoteId = conversation.included.privateNote && conversation.included.privateNote.id
      const noteResource = buildPrivateNoteResource(noteId || existingNoteId, noteAttributes)
      conversation.included.privateNote = noteResource
      setEoiPrivateNoteRelationship(eoi, noteResource)
      return
    }

    conversation.included.privateNote = null
    setEoiPrivateNoteRelationship(eoi, null)
  })

  if (changed) {
    conversationStore.emitChange()
  }
}

function withConversationType(data) {
  return conversationType === 'direct_requests' ? { ...data, 'filter[type]': conversationType } : data
}

function getCategoryName(item) {
  const job = item.included && item.included.job

  return (job && job.attributes.subcategoryName) || 'Uncategorized'
}

const conversationStore = assign({}, EventEmitter.prototype, {
  fetchAll(url) {
    API.get(url, withConversationType({ include: 'eoi,job', 'page[size]': 15 }), 'CONVERSATIONS_FETCH_SUCCESS')
  },

  fetchFromStart(url) {
    API.get(url, withConversationType({ include: 'eoi,job', 'page[size]': 15 }), 'CONVERSATIONS_FETCH_SUCCESS')
  },

  refetchAll(url) {
    API.get(url, withConversationType({ include: 'eoi,job', 'page[size]': 15 }), 'CONVERSATIONS_REFETCH_SUCCESS')
  },

  fetchByJob(url, jobId) {
    API.get(url, withConversationType({ include: 'eoi,job', 'page[size]': 15, job_id: jobId }), 'CONVERSATIONS_FETCH_BY_JOB_SUCCESS')
  },

  fetchByQuery(url, queryParams) {
    API.get(url, withConversationType({ include: 'eoi,job', 'page[size]': 15, ...queryParams }), 'CONVERSATIONS_FETCH_BY_QUERY_SUCCESS', 'CONVERSATIONS_FETCH_BY_QUERY_FAILURE')
  },

  fetch(id) {
    API.get(`${window.App.url}/jobs_conversations/${id}`, { include: 'eoi,job' }, 'CONVERSATION_FETCH_SUCCESS')
  },

  fetchNext() {
    const params = jobId !== null ? { include: 'eoi,job', job_id: jobId } : { include: 'eoi,job' }

    if (sortKey) {
      params.sort = sortKey
    }

    API.get(links.next, withConversationType(params), 'CONVERSATIONS_FETCH_NEXT_SUCCESS')
  },

  allConversationsLoaded: () => allConversationsLoaded,
  get: id => conversations.filter((conversation) => conversation.id === id)[0],
  getAll: () => conversations,
  getCurrent: function () { return this.get(this.getOpenConversationID()) },
  getLoading: () => loading,
  getOpenConversationID: () => openConversationID,
  getCategoryName: item => getCategoryName(item),
  getPageContextType: () => pageContextType,

  emitChange() { this.emit(CHANGE_EVENT) },
  addChangeListener(callback) { this.on(CHANGE_EVENT, callback) },
  removeChangeListener(callback) { this.removeListener(CHANGE_EVENT, callback) },
  setSortKey(key) {
    sortKey = key

    if (sortKey) {
      conversations = sortConversations(conversations, sortKey)
      openConversationID = conversations[0].id
    }

    this.emitChange()
  },
  getSortKey: () => sortKey,
  updatePrivateNoteForEoi: (eoiId, noteAttributes, noteId) => {
    updatePrivateNoteForEoi(eoiId, noteAttributes, noteId)
  }
})

conversationStore.dispatchToken = chatDispatcher.register((payload) => {
  switch (payload.type) {
    case 'LOAD_CONVERSATIONS':
    case 'RELOAD_CONVERSATIONS':
      let sortFromPayload

      if (typeof payload.data === 'string') {
        pageContextType = payload.data
      } else {
        pageContextType = payload.data.pageContextType
        sortFromPayload = payload.data.sort
        sortKey = payload.data.sort
      }

      conversations = []
      query = null
      allConversationsLoaded = false
      loading = true
      const url = `${window.App.url}/jobs_conversations/${pageContextType}${buildPrimaryParam()}${sortFromPayload ? `?sort=${sortFromPayload}` : ``}`

      if (payload.type === 'LOAD_CONVERSATIONS') {
        if (sortFromPayload === 'job-category') {
          sortKey = 'job-category'
          conversationStore.fetchFromStart(url)
        }
        else {
          conversationStore.fetchAll(url)
        }
      } else {
        conversationStore.refetchAll(url)
      }

      break

    case 'CONVERSATIONS_FETCH_BY_JOB':
      jobId = payload.data
      conversationStore.fetchByJob(`${window.App.url}/jobs_conversations/user${buildPrimaryParam()}`, jobId)
      break

    case 'CONVERSATIONS_FETCH_BY_JOB_SUCCESS':
      var newConversations = payload.data.data
      var included = payload.data.included
      loading = false

      newConversations.map((newConversation, index) => {
        if (!conversationExists(newConversation.id)){
          newConversation = addIncludedDataToConversations(included, [newConversation])
          conversations = [...conversations, ...newConversation]
        }
      })

      links = payload.data.links

      if (!coreUtils.mobilecheck()) {
        openConversationID = newConversations[0].id
      }

      break

    case 'CONVERSATIONS_FETCH_BY_QUERY':
      query = payload.data
      conversations = []
      allConversationsLoaded = false
      loading = true
      conversationStore.fetchByQuery(`${window.App.url}/jobs_conversations/${conversationStore.getPageContextType()}${buildPrimaryParam()}`, query)
      break

    case 'CONVERSATIONS_FETCH_BY_QUERY_SUCCESS':
      var newConversations = payload.data.data
      var included = payload.data.included
      loading = false

      if (newConversations.length > 0){
        addIncludedDataToConversations(included, newConversations)

        links = payload.data.links

        if (!coreUtils.mobilecheck()) {
          openConversationID = newConversations[0].id
        }
      }

      allConversationsLoaded = newConversations.length < 15

      conversations = newConversations

      break

    case 'CONVERSATIONS_FETCH_BY_QUERY_FAILURE':
      loading = false
      allConversationsLoaded = true
      break

    case 'CONVERSATIONS_FETCH_SUCCESS':
      loading = false
      let primaryConversationID = window.location.pathname.match(/\/[0-9]+$/)

      if (!!payload.data.data.length) {
        var newConversations = payload.data.data
        var included = payload.data.included

        const hasIncluded = addIncludedDataToConversations(included, newConversations)

        conversations = sortKey ? sortConversations(hasIncluded, sortKey) : hasIncluded

        links = payload.data.links
        allConversationsLoaded = newConversations.length < 15

        if(!coreUtils.mobilecheck()) {
          openConversationID = conversations[0].id
        }
      } else {
        conversations = []
        allConversationsLoaded = true
      }

      break

    case 'CONVERSATIONS_REFETCH_SUCCESS':
      loading = false

      if(!!payload.data.data.length) {
        var newConversations = payload.data.data
        var included = payload.data.included

        const hasIncluded = addIncludedDataToConversations(included, newConversations)
        conversations = sortKey ? sortConversations(hasIncluded, sortKey) : hasIncluded

        links = payload.data.links
        allConversationsLoaded = newConversations.length < 15
        openConversationID = newConversations[0].id
      } else {
        conversations = []
        allConversationsLoaded = true
      }
      break

    case 'CONVERSATION_FETCH_SUCCESS':
    case 'NEW_CONVERSATION_RECEIVED':
      updateConversations(payload)
      break

    case 'CONVERSATIONS_FETCH_NEXT':
      if (!loading && links.next) {
        loading = true
        conversationStore.fetchNext()
      }
      else {
        loading = false
        allConversationsLoaded = true
      }
      break

    case 'CONVERSATIONS_FETCH_NEXT_SUCCESS':
      loading = false
      const nextConvs = addIncludedDataToConversations(payload.data.included, payload.data.data)
      conversations = [...conversations, ...nextConvs]

      if (sortKey) {
        conversations = sortConversations(conversations, sortKey)
      }

      links = payload.data.links
      allConversationsLoaded = nextConvs.length < 15
      console.log('allConversationsLoaded: ', allConversationsLoaded)
      break

    case 'SET_CONVERSATION_TYPE':
      conversationType = payload.data
      break

    case 'UPDATE_OPEN_CONVERSATION_ID':
      openConversationID = payload.data
      break

    case 'MARK_ALL_AS_READ_SUCCESS':
      conversationStore.fetch(openConversationID)
      break

    case 'NEW_UNREAD_MESSAGE_RECEIVED':
      conversationStore.fetch(payload.data.data.relationships.jobsConversation.data.id)
      break

    case 'MOBILE_MESSAGE_BACK_BUTTON_PRESSED':
      openConversationID = null
      break

    default:
      return true
  }

  console.log('conversationsStore: ', payload.type)
  conversationStore.emitChange()
  return true
})

function buildPrimaryParam() {
  const match = window.location.pathname.match(/\/[0-9]+$/)
  return match ? `?primary_conversation_id=${match[0].replace(/^\//, '')}` : ''
}

export default conversationStore
